import { ReactEditor, RenderLeafProps } from 'slate-react';

import { BaseEditor, Descendant, Node } from 'slate';
import type { HistoryEditor } from 'slate-history';

import { ELEMENT_TAGS, TEXT_TAGS } from './constants.js';

type SlateNode = Descendant | { text: string };

type ExtendedLeaf = RenderLeafProps & {
  leaf: CustomLeaf;
};

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor;

export type CustomLeaf = {
  bold?: boolean;
  code?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  linethrough?: boolean;
  placeholder?: string;
};

export type CustomSlateNode = Node & {
  type: string;
};

export enum TextStyle {
  heading1 = 'style_heading1',
  heading2 = 'style_heading2',
  normal = 'style_normal',
  numbered = 'numbered-list',
  bulleted = 'bulleted-list',
}

export const stylesAttributes = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  linethrough: 'del',
};

export const extendedNodeTypes = {
  style_normal: 'p',
  style_heading1: 'h1',
  style_heading2: 'h2',
  'numbered-list': 'ol',
  'bulleted-list': 'ul',
  paragraph: 'p',
  'list-item': 'li',
};

export const getInitialValue = (
  fieldValue?: string,
): Array<Descendant & { type?: string }> => {
  if (fieldValue) {
    try {
      return JSON.parse(fieldValue);
    } catch (err) {
      return [
        {
          type: 'placeholder',
          children: [
            {
              text: fieldValue,
            },
          ],
        },
      ];
    }
  }

  return [
    {
      type: 'placeholder',
      children: [
        {
          text: '',
        },
      ],
    },
  ];
};

export const countTextLength = (node: any): number => {
  if (Array.isArray(node)) {
    return node.reduce((acc, n) => acc + countTextLength(n), 0);
  } else if (typeof node === 'object' && node.children) {
    return countTextLength(node.children);
  } else if (typeof node === 'object' && node.text) {
    return node.text.length;
  } else {
    return 0;
  }
};

export const getCssByTextStyle = (style: TextStyle | string) => {
  switch (style) {
    case TextStyle.heading1:
      return {
        fontWeight: 700,
        fontSize: '24px',
      };
    case TextStyle.heading2:
      return {
        fontWeight: 600,
        fontSize: '18px',
      };
    case TextStyle.bulleted:
      return {
        listStyle: 'disc',
        marginLeft: '1rem',
      };
    case TextStyle.numbered:
      return {
        listStyle: 'decimal',
        marginLeft: '1rem',
      };
    default:
      return {};
  }
};

const getStylesByLeaf = (leaf: CustomLeaf) => {
  const style = {} as React.CSSProperties;
  const decorations: string[] = [];
  if (leaf.bold) {
    style.fontWeight = 'bold';
  }
  if (leaf.code) {
    style.fontFamily = 'monospace';
  }
  if (leaf.italic) {
    style.fontStyle = 'italic';
  }
  if (leaf.underline) {
    decorations.push('underline');
  }
  if (leaf.linethrough) {
    decorations.push('line-through');
  }

  if (decorations.length > 0) {
    style.textDecoration = decorations.join(' ');
  }
  return style;
};

export const renderEditorLeaf = ({
  children,
  leaf,
  attributes,
}: ExtendedLeaf) => {
  if (leaf.placeholder) {
    return <span {...attributes}>{children}</span>;
  }

  return (
    <span {...attributes} style={getStylesByLeaf(leaf)}>
      {children}
    </span>
  );
};

export const htmlToSlateNodes = (el: HTMLElement | ChildNode): SlateNode[] => {
  if (el.nodeType === TEXT_NODE) {
    return [{ text: el.textContent ?? '' }];
    // Skip non-element nodes
  } else if (el.nodeType !== ELEMENT_NODE) {
    return [{ text: '' }];
    // Handle line breaks for proper newline format
  } else if (el.nodeName === 'BR') {
    return [{ text: '\n' }];
  }

  const { nodeName } = el;
  let parent = el;

  // Special case for <pre><code> blocks
  if (nodeName === 'PRE' && el.childNodes[0]?.nodeName === 'CODE') {
    parent = el.childNodes[0];
  }

  // Recursively process child nodes and remove null values
  const children: SlateNode[] = Array.from(parent.childNodes)
    .flatMap(htmlToSlateNodes)
    .filter((child): child is SlateNode => child !== null);

  // Push empty text node if there are no children
  if (children.length === 0) {
    children.push({ text: '' });
  }

  // Return children if the current node is the body, so we can process them further
  if (el.nodeName === 'BODY') {
    return children;
  }

  // Handle element nodes based on their tag
  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el as HTMLElement);
    return [{ ...attrs, children }];
  }

  // Handle text nodes based on their tag
  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName]();
    return children.map((child) => ({ ...attrs, ...child }));
  }

  return children;
};
